/**
 * Utility for detecting and filtering duplicate bank transactions
 * Uses composite key: company_id + account_id + transaction_date + amount + description
 */

interface TransactionToCheck {
  company_id: string;
  account_id: string;
  transaction_date: string;
  amount: number;
  description: string | null;
}

interface FilterResult<T extends TransactionToCheck> {
  newTransactions: T[];
  duplicateCount: number;
}

/**
 * Builds a composite key for duplicate detection
 * Format: account_id|date|amount|description_normalized
 * 
 * Normalization includes:
 * - Lowercase conversion
 * - Whitespace normalization (multiple spaces → single space)
 * - Special character removal (except hyphens)
 * - Amount precision normalization (2 decimal places)
 */
function buildCompositeKey(tx: TransactionToCheck): string {
  const normalizedDesc = (tx.description || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')           // Normalize whitespace (multiple spaces → single space)
    .replace(/[^\w\s-]/g, '');      // Remove special chars except hyphens and alphanumeric
  
  // Normalize amount to 2 decimal places to avoid floating point precision issues
  // e.g., 100.0, 100.00, 100 all become "100.00"
  const normalizedAmount = tx.amount.toFixed(2);
  
  return `${tx.account_id}|${tx.transaction_date}|${normalizedAmount}|${normalizedDesc}`;
}

/**
 * Filters out duplicate transactions from the provided array
 * 
 * @param transactions - Array of transactions to check for duplicates
 * @param supabaseClient - Supabase client instance
 * @returns Object containing filtered transactions and duplicate count
 */
export async function filterDuplicateTransactions<T extends TransactionToCheck>(
  transactions: T[],
  supabaseClient: any
): Promise<FilterResult<T>> {
  if (transactions.length === 0) {
    return {
      newTransactions: [],
      duplicateCount: 0,
    };
  }

  // Get unique company_id (should be same for all, but handle edge case)
  const companyId = transactions[0]?.company_id;
  if (!companyId) {
    console.warn('No company_id found in transactions, skipping duplicate check');
    return {
      newTransactions: transactions,
      duplicateCount: 0,
    };
  }

  // Get unique account IDs and date range
  const accountIds = [...new Set(transactions.map(tx => tx.account_id))];
  const dates = transactions.map(tx => tx.transaction_date).sort();
  const minDate = dates[0];
  const maxDate = dates[dates.length - 1];

  // Query existing transactions that might be duplicates
  // We query by company_id, account_ids, and date range for efficiency
  let query = supabaseClient
    .from('bank_transactions')
    .select('account_id, transaction_date, amount, description')
    .eq('company_id', companyId)
    .in('account_id', accountIds)
    .gte('transaction_date', minDate)
    .lte('transaction_date', maxDate);

  const { data: existingTransactions, error } = await query;

  if (error) {
    console.error('Error checking for duplicates:', error);
    // On error, return all transactions (fail open to avoid blocking imports)
    return {
      newTransactions: transactions,
      duplicateCount: 0,
    };
  }

  // Build a Set of composite keys for existing transactions
  const existingKeys = new Set<string>();
  if (existingTransactions) {
    for (const existing of existingTransactions) {
      const key = buildCompositeKey({
        company_id: companyId,
        account_id: existing.account_id,
        transaction_date: existing.transaction_date,
        amount: existing.amount,
        description: existing.description,
      });
      existingKeys.add(key);
    }
  }

  // Filter out duplicates
  const newTransactions: T[] = [];
  let duplicateCount = 0;

  for (const tx of transactions) {
    const key = buildCompositeKey(tx);
    if (existingKeys.has(key)) {
      duplicateCount++;
    } else {
      newTransactions.push(tx);
      // Add to set to prevent duplicates within the same batch
      existingKeys.add(key);
    }
  }

  return {
    newTransactions,
    duplicateCount,
  };
}
